import { SurchargeTypeEnums } from "Data/settings";
import { omitBy, isNil, isEmpty } from "lodash";
import moment from "moment";

const constructDateString = (date, strFormat = "DD-MM-YYYY", skipChecking = false) => {
  if (!date) return null;

  if (skipChecking) return date;

  const isMoment = moment(date).isValid();

  if (!isMoment) return null;

  return moment(date).format(strFormat);
};

const constructLocations = (dataLocations, locations) => {
  if (isEmpty(locations) && isEmpty(dataLocations)) return [];

  if (!isEmpty(locations)) {
    return locations.map((l) => {
      const ldata = dataLocations.find((dl) => dl.code === l);
      return {
        code: l,
        version: ldata.version,
      };
    });
  }

  return dataLocations.map((l) =>
    omitBy({ code: l.code, version: l.version }, (val) =>
      typeof val === "object" ? isEmpty(val) : isNil(val),
    ),
  );
};

const constructRates = (rates, version) => {
  if (isEmpty(rates)) return [];

  return rates.map((l) =>
    omitBy(
      {
        markupBeforeSST: l.markupBeforeSST,
        markupAfterSST: l.markupAfterSST,
        markupValue: l.markupValue,
        markupType: l.markupType,
        priceBeforeSST: l.priceBeforeSST,
        priceAfterSST: l.priceAfterSST,
        weightFrom: l.weightFrom,
        weightTo: l.weightTo,
        version: version,
      },
      (val) => (typeof val === "object" ? isEmpty(val) : isNil(val)),
    ),
  );
};

const constructSurcharge = (data) => {
  if (!data) return {};
  if (!data?.surchargeType) return {};

  // if the surcharge type is default, return as normal
  if (data.surchargeType === SurchargeTypeEnums.DEFAULT) return {};

  const common = {
    fixedAmount: data?.surchargeFixedAmount,
    percentage: data?.surchargePercentage,
  };

  if ([SurchargeTypeEnums.DISCOUNT,SurchargeTypeEnums.MARKUP].includes(data.surchargeType)) {
    return omitBy(
      {
        ...common,
        capAmount: data?.surchargeCapAmount,
      },
      isNil,
    );
  }

  // if there is
  return omitBy(common, isNil);
};

const model = (data) => {
  const nver = data?.version;
  const { isNewVersion } = data;

  const rateTableData = omitBy(
    {
      cutOffTime: constructDateString(data?.cutOffTime, "HH:mm", isNewVersion),
      estimatedDeliveryInDay: data?.estimatedDeliveryInDay,
      estimatedTime: constructDateString(data?.estimatedTime, "HH:mm", isNewVersion),
      // from the older files, locations can be both an array of strings and an array of objects =.=
      locations: constructLocations(data?.locationsData, data?.locations),
      name: data?.name,
      rates: constructRates(data?.rates, nver),
      surcharge: constructSurcharge(data),
      surchargeType: data?.surchargeType,
      validFrom: data?.validFrom,
      validTo: data?.validTo,
      version: data?.version,
    },
    (val) => (typeof val === "object" ? isEmpty(val) : isNil(val)),
  );

  const result = {
    _id: data?.settingID,
    active: data?.active ?? true,
    customerOrgID: data?.customerOrgID,
    data: rateTableData,
    orgID: data?.orgID,
    version: nver,
  };

  return omitBy(result, (val) => (typeof val === "object" ? isEmpty(val) : isNil(val)));
};

export default model;
